import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

const PaymentsDashboard = () => {
  const navigate = useNavigate();

  const [search, setSearch] = useState("");
  const [paymentStatus, setPaymentStatus] = useState("ALL");

  // =========================================================
  // SAMPLE PAYMENT DATA
  // Replace this with API data later
  // =========================================================

  const [payments] = useState([
    {
      id: 1001,
      userName: "Varun Tej",
      email: "varun12@gmail.com",
      bedName: "F1-R1-B1",
      amount: 5000,
      paymentMode: "ONLINE",
      paymentType: "MONTH",
      status: "RECEIVED",
      paymentDate: "01-10-2026",
    },
    {
      id: 1002,
      userName: "Rahul Kumar",
      email: "rahul@gmail.com",
      bedName: "F1-R1-B2",
      amount: 4500,
      paymentMode: "CASH",
      paymentType: "MONTH",
      status: "RECEIVED",
      paymentDate: "01-10-2026",
    },
    {
      id: 1003,
      userName: "Suresh Reddy",
      email: "suresh@gmail.com",
      bedName: "F1-R2-B1",
      amount: 4000,
      paymentMode: "ONLINE",
      paymentType: "MONTH",
      status: "PENDING",
      paymentDate: "02-10-2026",
    },
    {
      id: 1004,
      userName: "Kiran Kumar",
      email: "kiran@gmail.com",
      bedName: "F2-R1-B3",
      amount: 5500,
      paymentMode: "BANK_TRANSFER",
      paymentType: "MONTH",
      status: "RECEIVED",
      paymentDate: "02-10-2026",
    },
    {
      id: 1005,
      userName: "Praveen",
      email: "praveen@gmail.com",
      bedName: "F2-R2-B1",
      amount: 3500,
      paymentMode: "ONLINE",
      paymentType: "MONTH",
      status: "PENDING",
      paymentDate: "03-10-2026",
    },
  ]);

  // =========================================================
  // DAY WISE DATA
  // =========================================================

  const dayWisePayments = [
    {
      date: "01 Oct",
      amount: 9500,
      transactions: 2,
    },
    {
      date: "02 Oct",
      amount: 9500,
      transactions: 2,
    },
    {
      date: "03 Oct",
      amount: 3500,
      transactions: 1,
    },
  ];

  // =========================================================
  // MONTH WISE DATA
  // =========================================================

  const monthWisePayments = [
    {
      month: "Jun 2026",
      amount: 125000,
    },
    {
      month: "Jul 2026",
      amount: 142500,
    },
    {
      month: "Aug 2026",
      amount: 156000,
    },
    {
      month: "Sep 2026",
      amount: 168500,
    },
    {
      month: "Oct 2026",
      amount: 22500,
    },
  ];

  // =========================================================
  // PAYMENT SETTINGS
  // =========================================================

  const [settings, setSettings] = useState({
    paymentEnabled: true,
    onlinePayment: true,
    cashPayment: true,
    bankTransfer: true,
    qrPayment: true,
    paymentDueDay: 5,
    lateFeeEnabled: false,
    lateFeeAmount: 100,
  });

  // =========================================================
  // CALCULATIONS
  // =========================================================

  const totalAmount = useMemo(() => {
    return payments.reduce(
      (total, payment) => total + Number(payment.amount || 0),
      0,
    );
  }, [payments]);

  const receivedAmount = useMemo(() => {
    return payments
      .filter((payment) => payment.status === "RECEIVED")
      .reduce((total, payment) => total + Number(payment.amount || 0), 0);
  }, [payments]);

  const pendingAmount = useMemo(() => {
    return payments
      .filter((payment) => payment.status === "PENDING")
      .reduce((total, payment) => total + Number(payment.amount || 0), 0);
  }, [payments]);

  const receivedCount = payments.filter(
    (payment) => payment.status === "RECEIVED",
  ).length;

  const pendingCount = payments.filter(
    (payment) => payment.status === "PENDING",
  ).length;

  const totalReferralAmount = 12500;

  // =========================================================
  // FILTER PAYMENTS
  // =========================================================

  const filteredPayments = useMemo(() => {
    return payments.filter((payment) => {
      const searchText = search.toLowerCase();

      const matchesSearch =
        payment.userName.toLowerCase().includes(searchText) ||
        payment.email.toLowerCase().includes(searchText) ||
        payment.bedName.toLowerCase().includes(searchText) ||
        payment.paymentMode.toLowerCase().includes(searchText);

      const matchesStatus =
        paymentStatus === "ALL" || payment.status === paymentStatus;

      return matchesSearch && matchesStatus;
    });
  }, [payments, search, paymentStatus]);

  // =========================================================
  // CURRENCY
  // =========================================================

  const formatAmount = (amount) => {
    return `₹${Number(amount || 0).toLocaleString("en-IN")}`;
  };

  // =========================================================
  // SETTINGS TOGGLE
  // =========================================================

  const handleSettingChange = (key) => {
    setSettings((previous) => ({
      ...previous,
      [key]: !previous[key],
    }));
  };

  return (
    <>
      <style>{`

        * {
          box-sizing: border-box;
        }

        .payments-dashboard {
          min-height: 100vh;
          padding: 20px;
          background: #f5f7fb;
          color: #1e293b;
          font-family: Arial, Helvetica, sans-serif;
        }

        /* =====================================================
           HEADER
        ===================================================== */

        .payments-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 15px;
          margin-bottom: 20px;
        }

        .header-left {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .payment-icon {
          width: 44px;
          height: 44px;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #1e3a8a;
          color: white;
          font-size: 21px;
        }

        .dashboard-title {
          margin: 0;
          font-size: 22px;
          font-weight: 700;
          color: #172554;
        }

        .dashboard-subtitle {
          margin: 4px 0 0;
          font-size: 12px;
          color: #64748b;
        }

        .dashboard-btn {
          padding: 9px 15px;
          border: none;
          border-radius: 8px;
          background: #1e3a8a;
          color: #fff;
          font-size: 13px;
          font-weight: 600;
          cursor: pointer;
          transition: 0.2s;
          white-space: nowrap;
        }

        .dashboard-btn:hover {
          background: #172554;
          transform: translateY(-1px);
        }

        /* =====================================================
           SUMMARY CARDS
        ===================================================== */

        .summary-grid {
          display: grid;
          grid-template-columns: repeat(5, 1fr);
          gap: 14px;
          margin-bottom: 20px;
        }

        .summary-card {
          background: white;
          border: 1px solid #e2e8f0;
          border-radius: 11px;
          padding: 15px;
          box-shadow: 0 2px 7px rgba(15, 23, 42, 0.05);
        }

        .summary-card-top {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 10px;
        }

        .summary-label {
          font-size: 12px;
          color: #64748b;
          font-weight: 600;
        }

        .summary-icon {
          width: 30px;
          height: 30px;
          border-radius: 7px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #eff6ff;
          color: #2563eb;
          font-size: 14px;
        }

        .summary-value {
          font-size: 21px;
          font-weight: 700;
          color: #0f172a;
        }

        .summary-footer {
          margin-top: 5px;
          font-size: 11px;
          color: #94a3b8;
        }

        .received-card .summary-icon {
          background: #ecfdf5;
          color: #059669;
        }

        .pending-card .summary-icon {
          background: #fff7ed;
          color: #ea580c;
        }

        .referral-card .summary-icon {
          background: #f5f3ff;
          color: #7c3aed;
        }

        /* =====================================================
           MAIN GRID
        ===================================================== */

        .main-grid {
          display: grid;
          grid-template-columns: minmax(0, 2fr) minmax(300px, 1fr);
          gap: 18px;
          margin-bottom: 18px;
        }

        .card {
          background: white;
          border: 1px solid #e2e8f0;
          border-radius: 11px;
          box-shadow: 0 2px 7px rgba(15, 23, 42, 0.05);
          overflow: hidden;
        }

        .card-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 14px 16px;
          border-bottom: 1px solid #e2e8f0;
          gap: 10px;
        }

        .card-title {
          margin: 0;
          font-size: 14px;
          font-weight: 700;
          color: #1e293b;
        }

        .card-subtitle {
          margin: 3px 0 0;
          font-size: 11px;
          color: #94a3b8;
        }

        .card-body {
          padding: 15px;
        }

        /* =====================================================
           SEARCH
        ===================================================== */

        .filters {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 12px;
        }

        .search-input {
          flex: 1;
          min-width: 150px;
          padding: 9px 11px;
          border: 1px solid #cbd5e1;
          border-radius: 7px;
          outline: none;
          font-size: 12px;
        }

        .search-input:focus {
          border-color: #2563eb;
          box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.08);
        }

        .status-select {
          padding: 9px 10px;
          border: 1px solid #cbd5e1;
          border-radius: 7px;
          background: white;
          outline: none;
          font-size: 12px;
          color: #334155;
        }

        /* =====================================================
           TABLE
        ===================================================== */

        .table-wrapper {
          width: 100%;
          overflow-x: auto;
        }

        .payment-table {
          width: 100%;
          border-collapse: collapse;
          min-width: 720px;
        }

        .payment-table th {
          padding: 9px 8px;
          text-align: left;
          background: #f8fafc;
          color: #64748b;
          font-size: 10px;
          font-weight: 700;
          text-transform: uppercase;
          border-bottom: 1px solid #e2e8f0;
          white-space: nowrap;
        }

        .payment-table td {
          padding: 10px 8px;
          border-bottom: 1px solid #f1f5f9;
          font-size: 11px;
          color: #334155;
          white-space: nowrap;
        }

        .payment-table tr:hover {
          background: #f8fafc;
        }

        .user-name {
          font-weight: 700;
          color: #1e293b;
        }

        .user-email {
          font-size: 10px;
          color: #94a3b8;
          margin-top: 2px;
        }

        .amount {
          font-weight: 700;
          color: #0f172a;
        }

        .status-badge {
          display: inline-flex;
          align-items: center;
          padding: 4px 8px;
          border-radius: 20px;
          font-size: 9px;
          font-weight: 700;
        }

        .status-received {
          background: #dcfce7;
          color: #166534;
        }

        .status-pending {
          background: #ffedd5;
          color: #c2410c;
        }

        .mode-badge {
          padding: 4px 7px;
          border-radius: 5px;
          background: #f1f5f9;
          color: #475569;
          font-size: 9px;
          font-weight: 600;
        }

        /* =====================================================
           PAYMENT SETTINGS
        ===================================================== */

        .settings-list {
          display: flex;
          flex-direction: column;
          gap: 13px;
        }

        .setting-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 10px;
          padding-bottom: 12px;
          border-bottom: 1px solid #f1f5f9;
        }

        .setting-row:last-child {
          border-bottom: none;
          padding-bottom: 0;
        }

        .setting-name {
          font-size: 12px;
          font-weight: 600;
          color: #334155;
        }

        .setting-description {
          margin-top: 2px;
          font-size: 10px;
          color: #94a3b8;
        }

        .toggle {
          position: relative;
          width: 38px;
          height: 20px;
          flex-shrink: 0;
        }

        .toggle input {
          opacity: 0;
          width: 0;
          height: 0;
        }

        .toggle-slider {
          position: absolute;
          cursor: pointer;
          inset: 0;
          background: #cbd5e1;
          border-radius: 20px;
          transition: 0.2s;
        }

        .toggle-slider:before {
          content: "";
          position: absolute;
          width: 16px;
          height: 16px;
          left: 2px;
          top: 2px;
          background: white;
          border-radius: 50%;
          transition: 0.2s;
          box-shadow: 0 1px 3px rgba(0,0,0,0.2);
        }

        .toggle input:checked + .toggle-slider {
          background: #2563eb;
        }

        .toggle input:checked + .toggle-slider:before {
          transform: translateX(18px);
        }

        .due-day {
          width: 55px;
          padding: 6px;
          text-align: center;
          border: 1px solid #cbd5e1;
          border-radius: 6px;
          font-size: 11px;
        }

        /* =====================================================
           CHART SECTION
        ===================================================== */

        .charts-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 18px;
          margin-bottom: 18px;
        }

        .chart-container {
          padding: 15px;
        }

        .chart-bars {
          height: 190px;
          display: flex;
          align-items: flex-end;
          gap: 12px;
          padding: 10px 5px 0;
          border-bottom: 1px solid #cbd5e1;
        }

        .bar-item {
          flex: 1;
          height: 100%;
          display: flex;
          flex-direction: column;
          justify-content: flex-end;
          align-items: center;
          min-width: 35px;
        }

        .bar-value {
          font-size: 9px;
          font-weight: 700;
          color: #475569;
          margin-bottom: 5px;
          white-space: nowrap;
        }

        .bar {
          width: 60%;
          max-width: 38px;
          min-height: 5px;
          border-radius: 5px 5px 0 0;
          background: #2563eb;
          transition: 0.2s;
        }

        .bar:hover {
          background: #1d4ed8;
        }

        .bar-label {
          margin-top: 7px;
          font-size: 9px;
          color: #64748b;
          white-space: nowrap;
        }

        /* =====================================================
           RECEIVED / PENDING
        ===================================================== */

        .status-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 12px;
        }

        .status-box {
          padding: 15px;
          border-radius: 9px;
          border: 1px solid #e2e8f0;
        }

        .status-box.received {
          background: #f0fdf4;
          border-color: #bbf7d0;
        }

        .status-box.pending {
          background: #fff7ed;
          border-color: #fed7aa;
        }

        .status-box-title {
          font-size: 11px;
          font-weight: 700;
          color: #64748b;
          margin-bottom: 5px;
        }

        .status-box-amount {
          font-size: 20px;
          font-weight: 700;
        }

        .received .status-box-amount {
          color: #15803d;
        }

        .pending .status-box-amount {
          color: #c2410c;
        }

        .status-box-count {
          margin-top: 3px;
          font-size: 10px;
          color: #94a3b8;
        }

        /* =====================================================
           FOOTER
        ===================================================== */

        .dashboard-footer {
          text-align: center;
          padding: 10px;
          color: #94a3b8;
          font-size: 10px;
        }

        /* =====================================================
           RESPONSIVE
        ===================================================== */

        @media (max-width: 1100px) {
          .summary-grid {
            grid-template-columns: repeat(3, 1fr);
          }

          .main-grid {
            grid-template-columns: 1fr;
          }
        }

        @media (max-width: 750px) {
          .payments-dashboard {
            padding: 12px;
          }

          .payments-header {
            align-items: flex-start;
            flex-direction: column;
          }

          .dashboard-btn {
            width: 100%;
          }

          .summary-grid {
            grid-template-columns: repeat(2, 1fr);
          }

          .charts-grid {
            grid-template-columns: 1fr;
          }
        }

        @media (max-width: 480px) {
          .summary-grid {
            grid-template-columns: 1fr;
          }

          .filters {
            flex-direction: column;
            align-items: stretch;
          }

          .search-input,
          .status-select {
            width: 100%;
          }

          .status-grid {
            grid-template-columns: 1fr;
          }

          .dashboard-title {
            font-size: 19px;
          }
        }

      `}</style>

      <div className="payments-dashboard">
        {/* =====================================================
            HEADER
        ===================================================== */}

        <div className="payments-header">
          <div className="header-left">
            <div className="payment-icon">₹</div>

            <div>
              <h1 className="dashboard-title">Payments Dashboard</h1>

              <p className="dashboard-subtitle">
                Manage payments, received amounts and payment settings
              </p>
            </div>
          </div>

          <button
            className="dashboard-btn"
            onClick={() => navigate("/dashboard")}
          >
            ← Go to Dashboard
          </button>
        </div>

        {/* =====================================================
            SUMMARY CARDS
        ===================================================== */}

        <div className="summary-grid">
          <div className="summary-card">
            <div className="summary-card-top">
              <span className="summary-label">Total Payments</span>

              <div className="summary-icon">₹</div>
            </div>

            <div className="summary-value">{formatAmount(totalAmount)}</div>

            <div className="summary-footer">All payment transactions</div>
          </div>

          <div className="summary-card received-card">
            <div className="summary-card-top">
              <span className="summary-label">Total Received</span>

              <div className="summary-icon">✓</div>
            </div>

            <div className="summary-value">{formatAmount(receivedAmount)}</div>

            <div className="summary-footer">
              {receivedCount} received transactions
            </div>
          </div>

          <div className="summary-card pending-card">
            <div className="summary-card-top">
              <span className="summary-label">Pending Amount</span>

              <div className="summary-icon">!</div>
            </div>

            <div className="summary-value">{formatAmount(pendingAmount)}</div>

            <div className="summary-footer">
              {pendingCount} pending transactions
            </div>
          </div>

          <div className="summary-card">
            <div className="summary-card-top">
              <span className="summary-label">Transactions</span>

              <div className="summary-icon">#</div>
            </div>

            <div className="summary-value">{payments.length}</div>

            <div className="summary-footer">Total payment records</div>
          </div>

          <div className="summary-card referral-card">
            <div className="summary-card-top">
              <span className="summary-label">Referral Amount</span>

              <div className="summary-icon">↗</div>
            </div>

            <div className="summary-value">
              {formatAmount(totalReferralAmount)}
            </div>

            <div className="summary-footer">Total referral amount</div>
          </div>
        </div>

        {/* =====================================================
            USER PAYMENTS + SETTINGS
        ===================================================== */}

        <div className="main-grid">
          {/* USER PAYMENTS */}

          <div className="card">
            <div className="card-header">
              <div>
                <h2 className="card-title">User Payments</h2>

                <p className="card-subtitle">Payment transaction details</p>
              </div>

              <strong style={{ fontSize: "12px" }}>
                {filteredPayments.length} Records
              </strong>
            </div>

            <div className="card-body">
              <div className="filters">
                <input
                  type="text"
                  className="search-input"
                  placeholder="Search user, email, bed or payment mode..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />

                <select
                  className="status-select"
                  value={paymentStatus}
                  onChange={(e) => setPaymentStatus(e.target.value)}
                >
                  <option value="ALL">All Status</option>

                  <option value="RECEIVED">Received</option>

                  <option value="PENDING">Pending</option>
                </select>
              </div>

              <div className="table-wrapper">
                <table className="payment-table">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>User</th>
                      <th>Bed</th>
                      <th>Amount</th>
                      <th>Mode</th>
                      <th>Type</th>
                      <th>Status</th>
                      <th>Date</th>
                    </tr>
                  </thead>

                  <tbody>
                    {filteredPayments.length > 0 ? (
                      filteredPayments.map((payment) => (
                        <tr key={payment.id}>
                          <td>#{payment.id}</td>

                          <td>
                            <div className="user-name">{payment.userName}</div>

                            <div className="user-email">{payment.email}</div>
                          </td>

                          <td>{payment.bedName}</td>

                          <td className="amount">
                            {formatAmount(payment.amount)}
                          </td>

                          <td>
                            <span className="mode-badge">
                              {payment.paymentMode}
                            </span>
                          </td>

                          <td>{payment.paymentType}</td>

                          <td>
                            <span
                              className={
                                payment.status === "RECEIVED"
                                  ? "status-badge status-received"
                                  : "status-badge status-pending"
                              }
                            >
                              {payment.status}
                            </span>
                          </td>

                          <td>{payment.paymentDate}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td
                          colSpan="8"
                          style={{
                            textAlign: "center",
                            padding: "30px",
                            color: "#94a3b8",
                          }}
                        >
                          No payment records found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* PAYMENT SETTINGS */}

          <div className="card">
            <div className="card-header">
              <div>
                <h2 className="card-title">Payment Settings</h2>

                <p className="card-subtitle">Configure payment options</p>
              </div>
            </div>

            <div className="card-body">
              <div className="settings-list">
                <div className="setting-row">
                  <div>
                    <div className="setting-name">Payments Enabled</div>

                    <div className="setting-description">
                      Enable payment collection
                    </div>
                  </div>

                  <label className="toggle">
                    <input
                      type="checkbox"
                      checked={settings.paymentEnabled}
                      onChange={() => handleSettingChange("paymentEnabled")}
                    />

                    <span className="toggle-slider"></span>
                  </label>
                </div>

                <div className="setting-row">
                  <div>
                    <div className="setting-name">Online Payment</div>

                    <div className="setting-description">
                      Razorpay / online payments
                    </div>
                  </div>

                  <label className="toggle">
                    <input
                      type="checkbox"
                      checked={settings.onlinePayment}
                      onChange={() => handleSettingChange("onlinePayment")}
                    />

                    <span className="toggle-slider"></span>
                  </label>
                </div>

                <div className="setting-row">
                  <div>
                    <div className="setting-name">Cash Payment</div>

                    <div className="setting-description">
                      Allow cash payment
                    </div>
                  </div>

                  <label className="toggle">
                    <input
                      type="checkbox"
                      checked={settings.cashPayment}
                      onChange={() => handleSettingChange("cashPayment")}
                    />

                    <span className="toggle-slider"></span>
                  </label>
                </div>

                <div className="setting-row">
                  <div>
                    <div className="setting-name">Bank Transfer</div>

                    <div className="setting-description">
                      Allow bank transfer
                    </div>
                  </div>

                  <label className="toggle">
                    <input
                      type="checkbox"
                      checked={settings.bankTransfer}
                      onChange={() => handleSettingChange("bankTransfer")}
                    />

                    <span className="toggle-slider"></span>
                  </label>
                </div>

                <div className="setting-row">
                  <div>
                    <div className="setting-name">QR Code Payment</div>

                    <div className="setting-description">
                      Allow QR code payments
                    </div>
                  </div>

                  <label className="toggle">
                    <input
                      type="checkbox"
                      checked={settings.qrPayment}
                      onChange={() => handleSettingChange("qrPayment")}
                    />

                    <span className="toggle-slider"></span>
                  </label>
                </div>

                <div className="setting-row">
                  <div>
                    <div className="setting-name">Payment Due Day</div>

                    <div className="setting-description">
                      Monthly payment due date
                    </div>
                  </div>

                  <input
                    type="number"
                    min="1"
                    max="28"
                    className="due-day"
                    value={settings.paymentDueDay}
                    onChange={(e) =>
                      setSettings({
                        ...settings,
                        paymentDueDay: e.target.value,
                      })
                    }
                  />
                </div>

                <div className="setting-row">
                  <div>
                    <div className="setting-name">Late Fee</div>

                    <div className="setting-description">
                      Apply late payment fee
                    </div>
                  </div>

                  <label className="toggle">
                    <input
                      type="checkbox"
                      checked={settings.lateFeeEnabled}
                      onChange={() => handleSettingChange("lateFeeEnabled")}
                    />

                    <span className="toggle-slider"></span>
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* =====================================================
            DAY WISE + MONTH WISE
        ===================================================== */}

        <div className="charts-grid">
          {/* DAY WISE */}

          <div className="card">
            <div className="card-header">
              <div>
                <h2 className="card-title">Day-wise Amount Received</h2>

                <p className="card-subtitle">Daily payment collection</p>
              </div>
            </div>

            <div className="chart-container">
              <div className="chart-bars">
                {dayWisePayments.map((item, index) => {
                  const maxAmount = Math.max(
                    ...dayWisePayments.map((day) => day.amount),
                  );

                  const height = (item.amount / maxAmount) * 140;

                  return (
                    <div className="bar-item" key={index}>
                      <div className="bar-value">
                        {formatAmount(item.amount)}
                      </div>

                      <div
                        className="bar"
                        style={{
                          height: `${height}px`,
                        }}
                      ></div>

                      <div className="bar-label">{item.date}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* MONTH WISE */}

          <div className="card">
            <div className="card-header">
              <div>
                <h2 className="card-title">Monthly-wise Received</h2>

                <p className="card-subtitle">Monthly payment collection</p>
              </div>
            </div>

            <div className="chart-container">
              <div className="chart-bars">
                {monthWisePayments.map((item, index) => {
                  const maxAmount = Math.max(
                    ...monthWisePayments.map((month) => month.amount),
                  );

                  const height = (item.amount / maxAmount) * 140;

                  return (
                    <div className="bar-item" key={index}>
                      <div className="bar-value">
                        {formatAmount(item.amount)}
                      </div>

                      <div
                        className="bar"
                        style={{
                          height: `${height}px`,
                        }}
                      ></div>

                      <div className="bar-label">{item.month}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* =====================================================
            RECEIVED / PENDING
        ===================================================== */}

        <div className="card">
          <div className="card-header">
            <div>
              <h2 className="card-title">Payment Summary</h2>

              <p className="card-subtitle">
                Received and pending payment overview
              </p>
            </div>
          </div>

          <div className="card-body">
            <div className="status-grid">
              <div className="status-box received">
                <div className="status-box-title">TOTAL RECEIVED</div>

                <div className="status-box-amount">
                  {formatAmount(receivedAmount)}
                </div>

                <div className="status-box-count">
                  {receivedCount} transactions
                </div>
              </div>

              <div className="status-box pending">
                <div className="status-box-title">TOTAL PENDING</div>

                <div className="status-box-amount">
                  {formatAmount(pendingAmount)}
                </div>

                <div className="status-box-count">
                  {pendingCount} transactions
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="dashboard-footer">
          SVK PG Payment Management Dashboard
        </div>
      </div>
    </>
  );
};

export default PaymentsDashboard;
